// UK school year-group ↔ date helpers.
//
// English/Welsh school cutoff is 31 August. A child whose 5th birthday falls
// between 1 Sept (Y) and 31 Aug (Y+1) starts Reception in Sept (Y).
// Equivalently:
//   - Born Sept–Dec: Reception starts Sept of (DOB.year + 5)
//   - Born Jan–Aug:  Reception starts Sept of (DOB.year + 4)
//
// Year N (N=0 for Reception) runs Sept (RecStart + N) → July (RecStart + N + 1).

export const UK_YEAR_GROUPS = [
  { value: 'reception', label: 'Reception', index: 0 },
  { value: 'year_1', label: 'Year 1', index: 1 },
  { value: 'year_2', label: 'Year 2', index: 2 },
  { value: 'year_3', label: 'Year 3', index: 3 },
  { value: 'year_4', label: 'Year 4', index: 4 },
  { value: 'year_5', label: 'Year 5', index: 5 },
  { value: 'year_6', label: 'Year 6', index: 6 },
  { value: 'year_7', label: 'Year 7', index: 7 },
  { value: 'year_8', label: 'Year 8', index: 8 },
  { value: 'year_9', label: 'Year 9', index: 9 },
  { value: 'year_10', label: 'Year 10', index: 10 },
  { value: 'year_11', label: 'Year 11', index: 11 },
  { value: 'year_12', label: 'Year 12', index: 12 },
  { value: 'year_13', label: 'Year 13', index: 13 },
];

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Parse an ISO-ish DOB string (e.g. "2008-05-15") into {year, month}.
 * Returns null if invalid.
 */
function parseDob(dob) {
  if (!dob) return null;
  const m = String(dob).match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (!year || !month || month < 1 || month > 12) return null;
  return { year, month };
}

/**
 * Reception academic-year start (calendar year of the September).
 */
export function receptionStartYear(dob) {
  const parsed = parseDob(dob);
  if (!parsed) return null;
  // Sept-Dec → +5, Jan-Aug → +4
  return parsed.year + (parsed.month >= 9 ? 5 : 4);
}

/**
 * Resolve a year-group key (e.g. "year_10") to its index. Accepts the value or label.
 */
function findGroup(value) {
  if (value === null || value === undefined || value === '') return null;
  return (
    UK_YEAR_GROUPS.find((g) => g.value === value) ||
    UK_YEAR_GROUPS.find((g) => g.label.toLowerCase() === String(value).toLowerCase()) ||
    null
  );
}

/**
 * Given a DOB and a starting year group + (optional) ending year group,
 * compute approximate {start_date, end_date} as YYYY-MM strings.
 *
 * If no ending year group is given, end_date is the end of the same year.
 */
export function yearGroupsToDates(dob, startValue, endValue) {
  const recStart = receptionStartYear(dob);
  if (recStart === null) return { start_date: '', end_date: '' };
  const startGroup = findGroup(startValue);
  if (!startGroup) return { start_date: '', end_date: '' };
  const endGroup = findGroup(endValue) || startGroup;
  const startCalendarYear = recStart + startGroup.index;
  const endCalendarYear = recStart + endGroup.index + 1; // term ends following July
  return {
    start_date: `${startCalendarYear}-09`,
    end_date: `${endCalendarYear}-07`,
  };
}

/**
 * Pretty-print a YYYY-MM string as e.g. "Sept 2024".
 */
export function formatYearMonth(value) {
  if (!value) return '';
  const m = String(value).match(/^(\d{4})-(\d{2})/);
  if (!m) return value;
  const year = m[1];
  const month = parseInt(m[2], 10);
  if (month < 1 || month > 12) return value;
  return `${MONTH_NAMES[month - 1]} ${year}`;
}
